import {useMemo} from "react";
import _ from "lodash";
import {useEventsContext} from "@/pages/dashboard/EventsContext";
import SingleSiteHeader from "./SiteFilter/SingleSiteHeader";
import MultiSiteFilter from "./SiteFilter/MultiSiteFilter";
import SelectedSitesList from "@/pages/dashboard/SiteFilter/SelectedSitesList";

/**
 * EventSiteFilter component
 *
 * This component provides a site filtering interface for events.
 * It handles two main display modes:
 * 1. Single Site: Displays only the site name if only one site is available.
 * 2. Multi-Site: Displays a dropdown filter to select/deselect multiple sites.
 *
 * It also displays a list of currently selected sites with an option to reset filters
 * when in multi-site mode.
 */
export default function EventSiteFilter(props) {
  const {events, selectedSiteIds, setSelectedSiteIds} = useEventsContext();

  const siteCounts = useMemo(() => _.countBy(events, 'siteId'), [events]);

  const availableSites = useMemo(() => {
    const siteIds = _.uniq(_.map(events, 'siteId'));
    return _.orderBy(siteIds.map(id => ({
      id,
      name: _.find(events, ['siteId', id])?.siteName,
      itemCount: siteCounts[id]
    })), 'itemCount', 'desc');
  }, [events, siteCounts]);

  const filteredSites = useMemo(() => {
    return _.orderBy(selectedSiteIds.map(id => ({
      id,
      name: _.find(events, ['siteId', id])?.siteName,
      itemCount: siteCounts[id]
    })), 'itemCount', 'desc');
  }, [events, selectedSiteIds, siteCounts]);

  const toggleSite = (siteId) => {
    setSelectedSiteIds(prev => {
      if (prev.includes(siteId)) {
        return prev.filter(id => id !== siteId);
      } else {
        return [...prev, siteId];
      }
    });
  };
  const clearSites = () => {
    setSelectedSiteIds([]);
  }

  if (!availableSites?.length) return null;

  return (
    <div className="w-full flex gap-2">
      <div className="shrink-0">
        {availableSites.length === 1 && (
          <SingleSiteHeader site={availableSites[0]}/>
        )}
        {availableSites.length > 1 && (
          <MultiSiteFilter
            id="event-site-filter-btn"
            selectedSiteIds={selectedSiteIds}
            availableSites={availableSites}
            toggleSite={toggleSite}
            clearSites={clearSites}
          />
        )}
      </div>
      {availableSites.length > 1 && (
        <SelectedSitesList
          filteredSites={filteredSites}
          toggleSite={toggleSite}
          clearSites={clearSites}
        />
      )}
    </div>
  )
}




